import { useState } from 'react'
import {
  Check,
  MessageSquare,
  Sheet,
  Calendar,
  ListTodo,
  RefreshCw,
  Shield,
  LayoutGrid,
} from 'lucide-react'
import BrandLogo from '../components/BrandLogo'

/* ═══════════════════════════════════════════════════════════════════════════
   DIZEE — Product Deck
   Fullscreen scroll presentation: problems we solve, Sheets, Calendar, Tasks.
   Password-protected, same as /deck.
   ═══════════════════════════════════════════════════════════════════════════ */

const DECK_PASSWORD = 'dizee2025'

export default function ProductDeckPage() {
  const [authenticated, setAuthenticated] = useState(false)
  const [password, setPassword] = useState('')
  const [error, setError] = useState(false)

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault()
    if (password === DECK_PASSWORD) {
      setAuthenticated(true)
      setError(false)
    } else {
      setError(true)
    }
  }

  if (!authenticated) {
    return (
      <div className="fixed inset-0 flex items-center justify-center p-8" style={{ backgroundColor: '#13120A' }}>
        <div className="w-full max-w-md">
          <div className="mb-10">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-5 h-5 bg-[#FF2300] rounded-sm flex-shrink-0" />
              <BrandLogo className="h-3 w-auto text-white" />
            </div>
            <p className="text-sm text-zinc-500">Enter the password to view this deck.</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-sm text-zinc-300 mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  setError(false)
                }}
                placeholder="Password"
                autoFocus
                className="w-full rounded-lg border border-white/[0.08] bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-[#FF2300]/50 focus:border-transparent"
              />
              {error && (
                <p className="mt-2 text-xs text-red-400">Incorrect password. Try again.</p>
              )}
            </div>
            <button
              type="submit"
              className="w-full rounded-lg bg-white px-4 py-3 text-sm font-semibold text-zinc-950 hover:bg-zinc-100 transition-colors"
            >
              View Deck
            </button>
          </form>
          <p className="mt-8 text-[11px] text-zinc-700">
            Confidential — For intended recipients only.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: '#13120A' }}>
      <div className="max-w-[1400px] mx-auto px-8 sm:px-16 lg:px-24">
        {SLIDES.map(({ component: Section }, i) => (
          <section
            key={i}
            className={`py-24 sm:py-32 ${i === 0 ? 'min-h-screen flex flex-col justify-center' : ''} ${i < SLIDES.length - 1 ? 'border-b border-white/[0.04]' : ''}`}
          >
            <Section />
          </section>
        ))}
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   SLIDE DEFINITIONS
   ═══════════════════════════════════════════════════════════════════════════ */

interface Slide {
  title: string
  component: React.FC
}

function SlideTag({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-block text-[11px] font-semibold text-[#FF2300] uppercase tracking-widest">
      {children}
    </span>
  )
}

/* ── Slide 1: Title ────────────────────────────────────────────────────── */
function SlideTitle() {
  return (
    <div className="flex flex-col justify-center">
      <div className="flex items-center gap-2.5 mb-8">
        <div className="w-6 h-6 bg-[#FF2300] rounded-sm flex-shrink-0" />
        <BrandLogo className="h-4 w-auto text-white" />
      </div>
      <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white leading-[1.08] max-w-3xl mt-4">
        Product overview
      </h1>
      <p className="text-base sm:text-lg text-zinc-400 mt-6 max-w-xl">
        The operating system and source of truth for modern music careers — built so your team and your tools stay in sync.
      </p>
      <p className="text-sm text-zinc-500 mt-10">www.dizee.com</p>
    </div>
  )
}

/* ── Slide 2: Problems we solve ───────────────────────────────────────── */
function SlideProblems() {
  return (
    <div>
      <SlideTag>Problems we solve</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        Communication and data that actually stay in sync.
      </h2>

      <div className="mt-12 grid sm:grid-cols-2 gap-5">
        <ProductDeckCard
          icon={<MessageSquare className="w-5 h-5 text-[#FF2300]" />}
          title="Seamless communication"
          description="Keep internal and external team members aligned. No more digging through threads — everyone has access to the same source of truth, so communication stays seamless between your team and external partners (promoters, agents, labels)."
        />
        <ProductDeckCard
          icon={<RefreshCw className="w-5 h-5 text-[#FF2300]" />}
          title="Ticket counts that stay current"
          description="Keeping ticket counts up to date is a constant headache. Dizee keeps show data and ticket counts in sync everywhere — so you’re never working from stale numbers or updating the same info in multiple places."
        />
      </div>

      <div className="mt-10 rounded-xl border border-[#FF2300]/20 bg-[#FF2300]/[0.04] p-6 max-w-2xl">
        <p className="text-lg font-medium text-white">
          One source of truth. One place to look. No more version chaos.
        </p>
      </div>
    </div>
  )
}

/* ── Slide 3: Google Sheets integration ────────────────────────────────── */
function SlideSheets() {
  return (
    <div>
      <SlideTag>Google Sheets integration</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        Your show data, bidirectional sync.{' '}
        <span className="text-[#FF2300]">No workflow change.</span>
      </h2>

      <div className="mt-10 max-w-3xl">
        <p className="text-lg text-zinc-300 leading-relaxed">
          Our Google Sheets integration keeps all show data on Dizee in <strong className="text-white">bidirectional sync</strong> with your Google Sheets. You don’t have to change the way you work to keep Dizee updated — we meet you where you already are.
        </p>
      </div>

      <div className="mt-12 grid sm:grid-cols-2 gap-5">
        <ProductDeckCard
          icon={<Sheet className="w-5 h-5 text-[#FF2300]" />}
          title="One sheet, many tabs"
          description="Your sheets can have a separate tab for ticket counts that syncs across everything. Update in Sheets or in Dizee — it stays in sync everywhere."
        />
        <ProductDeckCard
          icon={<Check className="w-5 h-5 text-emerald-400" />}
          title="~2 minutes to set up"
          description="Setup takes about 2 minutes and stays synced. Connect once, and your show data and ticket counts flow between Dizee and Google Sheets automatically."
        />
      </div>

      <div className="mt-8 flex items-center gap-3 text-[13px] text-zinc-500">
        <RefreshCw className="w-4 h-4 text-[#FF2300]/70" />
        <span>Bidirectional sync • Show data • Ticket counts • No duplicate entry</span>
      </div>
    </div>
  )
}

/* ── Slide 4: Granular access & team views ─────────────────────────────── */
function SlideAccess() {
  return (
    <div>
      <SlideTag>Teams & access</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        Everyone sees only what they need.
      </h2>

      <div className="mt-10 max-w-3xl">
        <p className="text-lg text-zinc-300 leading-relaxed">
          Your whole team can have a <strong className="text-white">separate view for the things they work on only</strong>. Granular access settings mean less noise and fewer mistakes — if someone only works on shows, they only need to see shows; if someone only works on music, they only need to see music.
        </p>
      </div>

      <div className="mt-12 grid sm:grid-cols-3 gap-4">
        <AccessExample icon={<LayoutGrid className="w-5 h-5" />} role="Shows only" detail="Tour dates, ticket counts, promoters — nothing else." />
        <AccessExample icon={<LayoutGrid className="w-5 h-5" />} role="Music only" detail="Releases, catalog, credits — focused on music." />
        <AccessExample icon={<Shield className="w-5 h-5" />} role="Full access" detail="Managers and admins see the full career view." />
      </div>

      <div className="mt-8 rounded-lg border border-white/[0.06] bg-white/[0.02] p-4 max-w-xl">
        <p className="text-[13px] text-zinc-400">
          <strong className="text-zinc-300">Granular access</strong> — control who sees what. Fewer distractions, better focus, same source of truth.
        </p>
      </div>
    </div>
  )
}

/* ── Slide 5: Schedule & Google Calendar ───────────────────────────────── */
function SlideCalendar() {
  return (
    <div>
      <SlideTag>Schedule & Google Calendar</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        Your schedule, integrated with Google Calendar.
      </h2>

      <div className="mt-10 max-w-3xl">
        <p className="text-lg text-zinc-300 leading-relaxed">
          Schedule in Dizee is integrated directly with your Google Calendar. Add items from Dizee into your Google Calendar, create a shared calendar for your team, or choose different calendars — individual or shared — and see everything in one place.
        </p>
      </div>

      <ul className="mt-10 space-y-4 max-w-2xl">
        {[
          'Add show releases, music releases, and tasks from Dizee to your Google Calendar',
          'Shared calendar for the whole team, or pick specific calendars',
          'Toggle between “what’s on Google Calendar” and “everything on the artist’s career” so you can focus or zoom out',
        ].map((item, i) => (
          <li key={i} className="flex items-start gap-3">
            <Check className="w-5 h-5 text-[#FF2300] shrink-0 mt-0.5" />
            <span className="text-zinc-300">{item}</span>
          </li>
        ))}
      </ul>

      <div className="mt-10 flex items-center gap-3 rounded-xl border border-[#FF2300]/15 bg-[#FF2300]/[0.04] p-5 max-w-xl">
        <Calendar className="w-6 h-6 text-[#FF2300] shrink-0" />
        <p className="text-[14px] text-zinc-300">
          One schedule view: Dizee events, tasks, releases, and your existing Google Calendar — all in sync.
        </p>
      </div>
    </div>
  )
}

/* ── Slide 6: Tasks ────────────────────────────────────────────────────── */
function SlideTasks() {
  return (
    <div>
      <SlideTag>Tasks</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        Tasks that stay up to date on your schedule.
      </h2>

      <div className="mt-10 max-w-3xl">
        <p className="text-lg text-zinc-300 leading-relaxed">
          Tasks in Dizee stay up to date on your schedule. You can add tasks from Dizee straight into your Google Calendar so deadlines and to-dos live alongside your shows and releases — one place to see what’s due and when.
        </p>
      </div>

      <div className="mt-12 grid sm:grid-cols-2 gap-5">
        <ProductDeckCard
          icon={<ListTodo className="w-5 h-5 text-[#FF2300]" />}
          title="Tasks on the schedule"
          description="All tasks stay current on your schedule view. No more separate task lists that drift out of sync with reality."
        />
        <ProductDeckCard
          icon={<Calendar className="w-5 h-5 text-[#FF2300]" />}
          title="Tasks → Google Calendar"
          description="Add tasks from Dizee to your Google Calendar. Your team can see what’s due in the same calendar they already use."
        />
      </div>
    </div>
  )
}

/* ── Slide 7: Summary ──────────────────────────────────────────────────── */
function SlideSummary() {
  return (
    <div>
      <SlideTag>Summary</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        One system. Your workflow. Stay in sync.
      </h2>

      <div className="mt-12 grid sm:grid-cols-2 gap-4">
        {[
          { label: 'Communication', value: 'Seamless between internal & external teams' },
          { label: 'Ticket counts', value: 'Always up to date, synced everywhere' },
          { label: 'Google Sheets', value: 'Bidirectional sync, ~2 min setup, ticket tab included' },
          { label: 'Access', value: 'Granular views — shows only, music only, or full career' },
          { label: 'Google Calendar', value: 'Schedule + releases + tasks, shared or personal' },
          { label: 'Tasks', value: 'On your schedule and in Google Calendar' },
        ].map((item, i) => (
          <div key={i} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5">
            <p className="text-[11px] font-semibold text-[#FF2300] uppercase tracking-widest mb-1">{item.label}</p>
            <p className="text-[14px] text-zinc-300">{item.value}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

/* ── Slide 8: Closing ───────────────────────────────────────────────────── */
function SlideClosing() {
  return (
    <div className="flex flex-col justify-center py-12">
      <div className="flex items-center gap-2.5 mb-8">
        <div className="w-6 h-6 bg-[#FF2300] rounded-sm flex-shrink-0" />
        <BrandLogo className="h-4 w-auto text-white" />
      </div>
      <div className="max-w-2xl">
        <p className="text-xl sm:text-2xl font-medium tracking-tight text-zinc-300 leading-snug">
          The operating system and source of truth for modern music careers — without changing how you work.
        </p>
      </div>
      <div className="mt-12 space-y-2">
        <p className="text-[14px] text-[#FF2300] font-medium">www.dizee.com</p>
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   SLIDE REGISTRY
   ═══════════════════════════════════════════════════════════════════════════ */

const SLIDES: Slide[] = [
  { title: 'Title', component: SlideTitle },
  { title: 'Problems we solve', component: SlideProblems },
  { title: 'Google Sheets', component: SlideSheets },
  { title: 'Teams & access', component: SlideAccess },
  { title: 'Schedule & Calendar', component: SlideCalendar },
  { title: 'Tasks', component: SlideTasks },
  { title: 'Summary', component: SlideSummary },
  { title: 'Closing', component: SlideClosing },
]

/* ═══════════════════════════════════════════════════════════════════════════
   REUSABLE COMPONENTS
   ═══════════════════════════════════════════════════════════════════════════ */

function ProductDeckCard({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode
  title: string
  description: string
}) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-6">
      <div className="mb-3">{icon}</div>
      <h3 className="text-base font-semibold text-white mb-2">{title}</h3>
      <p className="text-[15px] text-zinc-400 leading-relaxed">{description}</p>
    </div>
  )
}

function AccessExample({
  icon,
  role,
  detail,
}: {
  icon: React.ReactNode
  role: string
  detail: string
}) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5">
      <div className="text-[#FF2300] mb-2">{icon}</div>
      <p className="text-[14px] font-semibold text-white mb-1">{role}</p>
      <p className="text-[12px] text-zinc-500">{detail}</p>
    </div>
  )
}
